import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { BrainCircuit, Workflow, CalendarHeart, Lightbulb, Target, Users, ArrowRight } from 'lucide-react';
/*
  SEO SPECIFICATION:
  Title: MindStream.ing Community — Master the Art of Mindstreaming
  Meta Desc: Join the private MindStream.ing community. Learn the art of getting thoughts out of your head and streamlining them into automated processes. Get your life back.
  Primary KW: AI business automation community
*/
export function Community() {
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 30
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  return <main className="pt-20 bg-dark-bg text-white min-h-screen font-inter" data-id="element-1400">
      {/* Hero Section */}
      <section className="relative pt-24 pb-32 px-6 bg-hero-gradient overflow-hidden min-h-[70vh] flex items-center" data-id="element-1401">
        {/* Ambient Glow Orbs */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-1402" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1403" />

        <div className="inner-container relative z-10 text-center max-w-4xl mx-auto" data-id="element-1404">
          <motion.div initial="hidden" animate="show" variants={staggerContainer} data-id="element-1405">
            <motion.span variants={fadeUp} className="eyebrow mx-auto" data-id="element-1406">
              The Community
            </motion.span>
            <motion.h1 variants={fadeUp} className="font-poppins font-extrabold text-5xl md:text-7xl leading-[1.1] mb-6" data-id="element-1407">
              Master the Art of <br data-id="element-1408" />
              <span className="text-gradient" data-id="element-1409">MindStream.ing</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-xl md:text-2xl text-steel mb-12 leading-relaxed max-w-3xl mx-auto" data-id="element-1410">
              Mindstreaming isn't just software—it's an action. It's the
              beautiful art of getting thoughts out of your mind and
              streamlining them into automated processes. And it's a skill we
              teach you here.
            </motion.p>
            <motion.div variants={fadeUp} data-id="element-1411">
              <a href="https://members.mindstream.ing/communities/groups/mindstreaming/home" target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2 text-lg px-10 py-5 shadow-[0_0_40px_rgba(124,58,237,0.3)] hover:shadow-[0_0_60px_rgba(124,58,237,0.5)]" data-id="element-1412">
                Join the Community <ArrowRight size={20} data-id="element-1413" />
              </a>
              <p className="text-sm text-steel mt-6" data-id="element-1414">
                Exclusive to active MindStream.ing clients.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple/30 to-transparent" data-id="element-1415" />

      {/* What You'll Learn Section */}
      <section className="py-24 px-6 relative" data-id="element-1416">
        <div className="inner-container max-w-7xl mx-auto" data-id="element-1417">
          <div className="text-center mb-16 max-w-3xl mx-auto" data-id="element-1418">
            <h2 className="font-poppins font-extrabold text-4xl md:text-5xl mb-6" data-id="element-1419">
              Learn to Work at the Highest Level
            </h2>
            <p className="text-lg text-steel" data-id="element-1420">
              Most people have never been able to work in this manner. The
              community is your training ground to leverage MR to its absolute
              maximum potential.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1421">
            {[{
            icon: <Lightbulb size={32} data-id="element-1422" />,
            title: 'Prompt Creation',
            desc: 'Learn how to talk to MR effectively. Master the nuances of giving instructions that yield perfect results every time.',
            color: 'text-amber-400',
            bg: 'bg-amber-400/10'
          }, {
            icon: <Target size={32} data-id="element-1423" />,
            title: 'Planning & Strategy',
            desc: 'Discover how to map out your entire business operations and translate them into automated Streams.',
            color: 'text-teal',
            bg: 'bg-teal/10'
          }, {
            icon: <Workflow size={32} data-id="element-1424" />,
            title: 'Streamlining Thoughts',
            desc: 'The core art of mindstreaming: taking the chaotic ideas in your head and turning them into structured, repeatable processes.',
            color: 'text-purple',
            bg: 'bg-purple/10'
          }, {
            icon: <BrainCircuit size={32} data-id="element-1425" />,
            title: 'Advanced Training',
            desc: 'Deep-dive learning events and announcements to keep you at the cutting edge of AI automation.',
            color: 'text-blue-400',
            bg: 'bg-blue-400/10'
          }, {
            icon: <Users size={32} data-id="element-1426" />,
            title: 'Peer Collaboration',
            desc: 'Connect with other business owners who are building their own assembly lines. Share strategies and wins.',
            color: 'text-pink-400',
            bg: 'bg-pink-400/10'
          }, {
            icon: <CalendarHeart size={32} data-id="element-1427" />,
            title: 'Getting Your Life Back',
            desc: 'The ultimate goal. Learn strategies to take weekends off, work every other Friday, and actually enjoy the business you built.',
            color: 'text-emerald-400',
            bg: 'bg-emerald-400/10'
          }].map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-colors" data-id="element-1428">
                <div className={`w-14 h-14 rounded-xl ${feature.bg} ${feature.color} flex items-center justify-center mb-6`} data-id="element-1429">
                  {feature.icon}
                </div>
                <h3 className="font-poppins font-bold text-xl mb-3 text-white" data-id="element-1430">
                  {feature.title}
                </h3>
                <p className="text-steel leading-relaxed" data-id="element-1431">{feature.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* The Goal Section */}
      <section className="py-24 px-6 relative overflow-hidden bg-section-gradient" data-id="element-1432">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-teal/10 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-1433" />

        <div className="inner-container max-w-4xl mx-auto relative z-10 text-center" data-id="element-1434">
          <h2 className="font-poppins font-extrabold text-4xl md:text-5xl mb-8" data-id="element-1435">
            The Ultimate Goal: <br data-id="element-1436" />
            <span className="text-gradient" data-id="element-1437">Getting Your Life Back</span>
          </h2>
          <p className="text-xl text-steel mb-12 leading-relaxed" data-id="element-1438">
            Mindstreaming isn't just about doing more work—it's about doing less
            of it yourself. Inside the community, we focus heavily on the
            lifestyle design aspect of automation. We'll teach you how to
            leverage MR so you can finally take weekends off, work every other
            Friday, and step away from the daily grind without your business
            missing a beat.
          </p>

          <a href="https://members.mindstream.ing/communities/groups/mindstreaming/home" target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2" data-id="element-1439">
            Start Learning Today <ArrowRight size={18} data-id="element-1440" />
          </a>
        </div>
      </section>
    </main>;
}